import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Namespace, ParseKeys, TFunction } from 'i18next';
import { Chip } from '@unmind/design-system';
import { PRACTITIONER_FILTERS_QUERY } from 'Services/BeGateway/Practitioner/practitioner.services';
import { BEGatewayQueryContext, defaultQueryConfig } from 'utils/apollo';
import { practitionerFiltersQuery } from 'Services/BeGateway/Practitioner/__generated__/practitionerFiltersQuery';
import { getISOLanguageFromCode } from 'utils/getISOLanguageFromCode';
import { tracking } from 'App/Tracking';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';
import { getUserCountryState_user as UserLocation } from 'Account/hooks/__generated__/getUserCountryState';
import {
  FilterActionMeta,
  FilterCategory,
  FilterOptionType,
  FiltersCollection,
} from 'Book/shared';
import { Dropdown } from '../Dropdown';
import { ClearButton, FilterPanelContainer, ChipsContainer } from './styles';

export interface BrowseFilterPanelProps {
  selectedFilters: FiltersCollection;
  onFiltersChange(filters: FiltersCollection): void;
  userLocation: UserLocation | null;
}

const queryResponseToFilterOption = (
  t: TFunction<Namespace<'book'>>,
  queryResponse: { name: string },
  keyPath = 'specialities',
) => ({
  value: queryResponse.name,
  label: t(`${keyPath}.${queryResponse.name}` as ParseKeys<Namespace<'book'>>),
});

export default React.memo(
  ({
    selectedFilters,
    onFiltersChange,
    userLocation,
  }: BrowseFilterPanelProps) => {
    const { t } = useTranslation('book', {
      keyPrefix: 'therapyAndCoaching.filters',
    });
    const { t: tPractitioner } = useTranslation('book', {
      keyPrefix: 'practitioner',
    });

    const { t: translateLanguageName } =
      useTranslation<Namespace<'languages'>>('languages');

    const genderAndEthnicityFilterEnabled = Boolean(
      useFeatureFlag(FEATURE_FLAGS.TALK_GENDER_AND_ETHNICITY),
    );

    const { data } = useQuery<practitionerFiltersQuery>(
      PRACTITIONER_FILTERS_QUERY,
      {
        variables: {
          types: ['THERAPIST', 'COACH'],
          country: userLocation?.country?.value,
          state: userLocation?.state?.value,
        },
        ...defaultQueryConfig,
        ...BEGatewayQueryContext,
        skip: !userLocation,
      },
    );

    const {
      languages: selectedLanguages,
      approaches: selectedApproaches,
      areasOfExpertise: selectedAreasOfExpertise,
      genders: selectedGenders,
      ethnicities: selectedEthnicities,
    } = selectedFilters;

    const allSelectedFiltersWithCategory: (FilterOptionType & {
      category: FilterCategory;
    })[] = selectedLanguages
      .map(lang => ({ ...lang, category: 'language' as FilterCategory }))
      .concat(
        selectedApproaches.map(approach => ({
          ...approach,
          category: 'approach' as FilterCategory,
        })),
      )
      .concat(
        selectedAreasOfExpertise.map(areaOfExpertise => ({
          ...areaOfExpertise,
          category: 'areaOfExpertise' as FilterCategory,
        })),
      )
      .concat(
        selectedGenders.map(gender => ({
          ...gender,
          category: 'gender' as FilterCategory,
        })),
      )
      .concat(
        selectedEthnicities.map(ethnicity => ({
          ...ethnicity,
          category: 'ethnicity' as FilterCategory,
        })),
      );

    // memoisation required to preserve select options
    const availableLanguages = useMemo(
      () =>
        data?.practitionerFilters?.languages?.map(lang => {
          const languageLabels = getISOLanguageFromCode(lang.name);

          return {
            value: lang.name,
            label: `${translateLanguageName(
              languageLabels.en as ParseKeys<Namespace<'languages'>>,
            )} (${languageLabels.native})`,
          };
        }) || [],
      [data, translateLanguageName],
    );

    const availableApproaches = useMemo(
      () =>
        data?.practitionerFilters?.approaches?.map(item =>
          queryResponseToFilterOption(tPractitioner, item),
        ) || [],
      [tPractitioner, data],
    );

    const availableAreasOfExpertise = useMemo(
      () =>
        data?.practitionerFilters?.areasOfExpertise?.map(item =>
          queryResponseToFilterOption(tPractitioner, item),
        ) || [],
      [tPractitioner, data],
    );

    const availableGenders = useMemo(
      () =>
        data?.practitionerFilters?.genders?.map(item =>
          queryResponseToFilterOption(t, item, 'gender.options'),
        ) || [],
      [t, data],
    );

    const availableEthnicities = useMemo(
      () =>
        data?.practitionerFilters?.ethnicities?.map(item =>
          queryResponseToFilterOption(t, item, 'ethnicity.options'),
        ) || [],
      [t, data],
    );

    /**
     * Handlers
     */
    const trackFilterCategoryClear = (
      filterCategory: 'all' | FilterCategory,
      uiElement: 'dropdown' | 'pill',
    ) => {
      tracking.track('talk-browse-practitioner-filter-option-selected', {
        filterOption: 'all',
        filterCategory,
        action: 'deselect',
        uiElement,
      });
    };

    const trackFilterValueClear = (
      filterOption: string | undefined,
      filterCategory: 'all' | FilterCategory,
      uiElement: 'dropdown' | 'pill',
    ) => {
      tracking.track('talk-browse-practitioner-filter-option-selected', {
        filterOption,
        filterCategory,
        action: 'deselect',
        uiElement,
      });
    };

    const trackFilterOptionChange = (
      actionMeta: FilterActionMeta | undefined,
      filterCategory: FilterCategory,
    ) => {
      if (actionMeta?.action === 'select-option') {
        tracking.track('talk-browse-practitioner-filter-option-selected', {
          filterOption: actionMeta?.option?.value,
          filterCategory,
          action: 'select',
          uiElement: 'dropdown',
        });
      }

      if (actionMeta?.action === 'deselect-option') {
        trackFilterValueClear(
          actionMeta?.option?.value,
          filterCategory,
          'dropdown',
        );
      }

      if (actionMeta?.action === 'clear') {
        trackFilterCategoryClear(filterCategory, 'dropdown');
      }
    };

    const clearValue = (value: string) => {
      onFiltersChange({
        languages: selectedLanguages.filter(s => s.value !== value),
        approaches: selectedApproaches.filter(s => s.value !== value),
        areasOfExpertise: selectedAreasOfExpertise.filter(
          s => s.value !== value,
        ),
        genders: selectedGenders.filter(s => s.value !== value),
        ethnicities: selectedEthnicities.filter(s => s.value !== value),
      });
    };

    const clearAllValues = () => {
      trackFilterCategoryClear('all', 'pill');
      onFiltersChange({
        languages: [],
        approaches: [],
        areasOfExpertise: [],
        genders: [],
        ethnicities: [],
      });
    };

    return (
      <>
        <FilterPanelContainer>
          {!!data && (
            <>
              <Dropdown
                placeholder={t('language.title')}
                description={t('language.description')}
                options={availableLanguages}
                value={selectedLanguages}
                onChange={(value, actionMeta) => {
                  trackFilterOptionChange(actionMeta, 'language');
                  onFiltersChange({
                    ...selectedFilters,
                    languages: [...value],
                  });
                }}
                onClear={() => {
                  trackFilterCategoryClear('language', 'dropdown');
                  onFiltersChange({
                    ...selectedFilters,
                    languages: [],
                  });
                }}
              />
              <Dropdown
                placeholder={t('focusArea.title')}
                description={t('focusArea.description')}
                options={availableAreasOfExpertise}
                value={selectedAreasOfExpertise}
                onChange={(value, actionMeta) => {
                  trackFilterOptionChange(actionMeta, 'areaOfExpertise');
                  onFiltersChange({
                    ...selectedFilters,
                    areasOfExpertise: [...value],
                  });
                }}
                onClear={() => {
                  trackFilterCategoryClear('areaOfExpertise', 'dropdown');
                  onFiltersChange({
                    ...selectedFilters,
                    areasOfExpertise: [],
                  });
                }}
              />
              <Dropdown
                placeholder={t('approach.title')}
                description={t('approach.description')}
                options={availableApproaches}
                value={selectedApproaches}
                onChange={(value, actionMeta) => {
                  trackFilterOptionChange(actionMeta, 'approach');
                  onFiltersChange({
                    ...selectedFilters,
                    approaches: [...value],
                  });
                }}
                onClear={() => {
                  trackFilterCategoryClear('approach', 'dropdown');
                  onFiltersChange({
                    ...selectedFilters,
                    approaches: [],
                  });
                }}
              />
              {genderAndEthnicityFilterEnabled &&
                availableGenders.length > 0 && (
                  <Dropdown
                    placeholder={t('gender.title')}
                    description={t('gender.description')}
                    footnote={t('identityFootnote')}
                    options={availableGenders}
                    value={selectedGenders}
                    onChange={(value, actionMeta) => {
                      trackFilterOptionChange(actionMeta, 'gender');
                      onFiltersChange({
                        ...selectedFilters,
                        genders: [...value],
                      });
                    }}
                    onClear={() => {
                      trackFilterCategoryClear('gender', 'dropdown');
                      onFiltersChange({
                        ...selectedFilters,
                        genders: [],
                      });
                    }}
                  />
                )}
              {genderAndEthnicityFilterEnabled &&
                availableEthnicities.length > 0 && (
                  <Dropdown
                    placeholder={t('ethnicity.title')}
                    description={t('ethnicity.description')}
                    footnote={t('identityFootnote')}
                    options={availableEthnicities}
                    value={selectedEthnicities}
                    onChange={(value, actionMeta) => {
                      trackFilterOptionChange(actionMeta, 'ethnicity');
                      onFiltersChange({
                        ...selectedFilters,
                        ethnicities: [...value],
                      });
                    }}
                    onClear={() => {
                      trackFilterCategoryClear('ethnicity', 'dropdown');
                      onFiltersChange({
                        ...selectedFilters,
                        ethnicities: [],
                      });
                    }}
                  />
                )}
            </>
          )}
        </FilterPanelContainer>
        {allSelectedFiltersWithCategory.length ? (
          <ChipsContainer>
            {allSelectedFiltersWithCategory.map(selectedFilter => (
              <Chip
                key={`${selectedFilter.value}-selected`}
                label={selectedFilter.label}
                initialActiveState
                showDeactivateIcon
                onDeactivate={() => {
                  trackFilterValueClear(
                    selectedFilter.value,
                    selectedFilter.category,
                    'pill',
                  );
                  clearValue(selectedFilter.value);
                }}
              />
            ))}
            <ClearButton onClick={clearAllValues}>
              {t('ctas.clear')}
            </ClearButton>
          </ChipsContainer>
        ) : null}
      </>
    );
  },
);
